import { useEffect, useState } from "react";
import { api, notifyError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Users, CurrencyEur, Rocket, TrendUp, ShieldWarning, Ticket, Copy, Power, PaperPlaneTilt, ChartLine, Sparkle, Eye, FileArrowDown, Bell, Funnel, MagnifyingGlass, NotePencil } from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from "recharts";

const money = (n) => "€" + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PLAN_LABEL = { pro_monthly: "Pro · monthly", pro_annual: "Pro · annual", firm_monthly: "Firm · monthly" };
// Mirror backend _csrc: untagged/landing signups group under the "direct" channel.
const csrc = (s) => { const v = (s || "landing"); return v === "landing" || v === "" ? "direct" : v; };

function Stat({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="glass rounded-2xl p-5" data-testid={`admin-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-white/45 text-xs uppercase tracking-wider mb-2">
        <Icon size={15} weight="fill" style={{ color: accent || "#5B8DEF" }} /> {label}
      </div>
      <div className="font-display text-3xl" style={{ color: accent || "#F3F5F4" }}>{value}</div>
      {sub && <div className="text-white/40 text-xs mt-1">{sub}</div>}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/billing/funnel").then((r) => setData(r.data)).catch(() => setErr(true));
  }, []);

  if (!user?.is_admin || err) {
    return (
      <div className="max-w-md mx-auto mt-20 glass rounded-2xl p-8 text-center" data-testid="admin-forbidden">
        <ShieldWarning size={40} weight="fill" className="mx-auto mb-3 text-[#E63946]" />
        <div className="font-display text-xl mb-1">Admin access only</div>
        <p className="text-white/50 text-sm">This operator dashboard isn&apos;t available on your account.</p>
      </div>
    );
  }

  if (!data) {
    return <div className="p-8 text-white/50" data-testid="admin-loading">Loading funnel…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-page">
      <div>
        <div className="eyebrow" style={{ color: "#FFB020" }}>Operator</div>
        <h1 className="font-display text-4xl">Billing funnel</h1>
        <p className="text-white/60 text-sm mt-2">How your monetisation is performing across all customers.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Users} label="Total users" value={data.total_users} sub={`${data.free_users} on Free`} />
        <Stat icon={CurrencyEur} label="Revenue" value={money(data.revenue_eur)} sub={`${data.purchases_count} purchases`} accent="#00A86B" />
        <Stat icon={TrendUp} label="Paid conversion" value={`${data.paid_conversion_pct}%`} sub={`${data.active_paid} active paid`} accent="#FFB020" />
        <Stat icon={Rocket} label="Accelerator" value={data.accelerator_redemptions} sub={`${data.active_accelerator} active · ${data.accelerator_to_paid} → paid`} />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="glass rounded-2xl p-6">
          <div className="eyebrow mb-4">Active plans</div>
          <div className="space-y-3 text-sm">
            {[["Free", data.free_users], ["Pro", data.pro_active], ["Firm", data.firm_active]].map(([label, count]) => {
              const pct = data.total_users ? Math.round((count / data.total_users) * 100) : 0;
              return (
                <div key={label} data-testid={`admin-plan-${label.toLowerCase()}`}>
                  <div className="flex justify-between mb-1"><span className="text-white/70">{label}</span><span className="mono">{count} · {pct}%</span></div>
                  <div className="h-2 rounded-full bg-white/8 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pct}%`, background: label === "Firm" ? "#FFB020" : label === "Pro" ? "#00A86B" : "#5B8DEF" }} /></div>
                </div>
              );
            })}
          </div>
          <div className="mt-5 pt-4 border-t border-white/8 text-xs text-white/50">
            Accelerator → paid conversion: <span className="text-[#FFB020] mono">{data.accelerator_to_paid_pct}%</span>
          </div>
        </div>

        <div className="glass rounded-2xl p-6">
          <div className="eyebrow mb-4">Revenue by plan</div>
          {Object.keys(data.by_plan || {}).length === 0 ? (
            <div className="text-white/40 text-sm py-6 text-center">No purchases yet.</div>
          ) : (
            <table className="data w-full">
              <thead><tr><th>Plan</th><th className="text-right">Sales</th><th className="text-right">Revenue</th></tr></thead>
              <tbody>
                {Object.entries(data.by_plan).map(([plan, v]) => (
                  <tr key={plan}><td>{PLAN_LABEL[plan] || plan}</td><td className="num">{v.count}</td><td className="num text-[#00A86B]">{money(v.revenue)}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="eyebrow mb-4">Recent purchases</div>
        {data.recent_purchases.length === 0 ? (
          <div className="text-white/40 text-sm py-6 text-center">No purchases yet.</div>
        ) : (
          <table className="data w-full" data-testid="admin-recent">
            <thead><tr><th>Date</th><th>Customer</th><th>Plan</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {data.recent_purchases.map((p, i) => (
                <tr key={`${p.date}-${p.email}-${i}`}><td className="mono text-xs">{p.date}</td><td>{p.email}</td><td>{PLAN_LABEL[p.plan_id] || p.plan_id}</td><td className="num text-[#00A86B]">{p.currency === "EUR" ? "€" : p.currency + " "}{Number(p.amount).toFixed(2)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <LaunchAdmin />
    </div>
  );
}

function LaunchAdmin() {
  const [codes, setCodes] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [form, setForm] = useState({ note: "", max_uses: "", expires_at: "" });
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sourceFilter, setSourceFilter] = useState(null);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [reminding, setReminding] = useState(null);
  const [remindAllBusy, setRemindAllBusy] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [segments, setSegments] = useState([]);
  const [segName, setSegName] = useState("");
  const [tab, setTab] = useState("overview");

  const loadFollowUps = async () => { try { const r = await api.get("/admin/follow-ups"); setFollowUps(r.data.items || []); } catch { /* noop */ } };
  const loadSegments = async () => { try { const r = await api.get("/admin/segments"); setSegments(r.data.items || []); } catch { /* noop */ } };

  const load = async () => {
    try {
      const [c, w, s, a] = await Promise.all([
        api.get("/admin/invite-codes"), api.get("/admin/waitlist"),
        api.get("/admin/access-stats"), api.get("/admin/access-analytics?days=30"),
      ]);
      setCodes(c.data.items || []); setWaitlist(w.data.items || []); setStats(s.data); setAnalytics(a.data);
    } catch (e) { notifyError(e); }
    loadFollowUps(); loadSegments();
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = { note: form.note };
      if (form.max_uses) payload.max_uses = Number(form.max_uses);
      if (form.expires_at) payload.expires_at = form.expires_at;
      const r = await api.post("/admin/invite-codes", payload);
      toast.success(`Invite code created: ${r.data.code}`);
      setForm({ note: "", max_uses: "", expires_at: "" });
      load();
    } catch (err) { notifyError(err); } finally { setCreating(false); }
  };
  const toggle = async (id) => { try { await api.post(`/admin/invite-codes/${id}/toggle`); load(); } catch (e) { notifyError(e); } };
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success("Code copied"); };
  const invite = async (w) => {
    setInviting(w.waitlist_id);
    try {
      const r = await api.post(`/admin/waitlist/${w.waitlist_id}/invite`);
      if (r.data.emailed) toast.success(`Invite emailed to ${r.data.to} (${r.data.code})`);
      else if (r.data.email_error === "domain not verified") toast.error(`Code ${r.data.code} created — email NOT sent: verify percen360.com at resend.com/domains. Copy the code and send it manually.`);
      else toast.message(`Code ${r.data.code} created — email not sent (${r.data.email_error || "failed"}). Copy the code and send it manually.`);
      load();
    } catch (e) { notifyError(e); } finally { setInviting(null); }
  };
  const pendingIds = waitlist.filter((w) => w.status === "pending").map((w) => w.waitlist_id);
  const awaitingCount = waitlist.filter((w) => w.status === "invited").length;
  const visibleLeads = (() => {
    let list = sourceFilter ? waitlist.filter((w) => csrc(w.source) === sourceFilter) : waitlist.slice();
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((w) => [w.name, w.company, w.email].some((f) => (f || "").toLowerCase().includes(q)));
    if (sortBy === "name") list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (sortBy === "company") list.sort((a, b) => (a.company || "").localeCompare(b.company || ""));
    else if (sortBy === "status") list.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
    return list;
  })();
  const filtering = Boolean(sourceFilter || search.trim());
  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.includes(id));
  const selectAll = () => setSelected(allSelected ? [] : pendingIds);
  const inviteBulk = async () => {
    if (selected.length === 0) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/waitlist/invite-bulk", { waitlist_ids: selected });
      toast.success(`Invited ${r.data.invited} lead${r.data.invited === 1 ? "" : "s"} (${r.data.emailed} emailed)`);
      setSelected([]); load();
    } catch (e) { notifyError(e); } finally { setBulkBusy(false); }
  };
  const remind = async (w) => {
    setReminding(w.waitlist_id);
    try {
      const r = await api.post(`/admin/waitlist/${w.waitlist_id}/remind`);
      if (r.data.emailed) toast.success(`Reminder emailed to ${r.data.to}`);
      else if (r.data.email_error === "domain not verified") toast.error("Reminder NOT sent — verify percen360.com at resend.com/domains.");
      else toast.message(`Reminder not sent (${r.data.email_error || "failed"}).`);
      load();
    } catch (e) { notifyError(e); } finally { setReminding(null); }
  };
  const remindAll = async () => {
    setRemindAllBusy(true);
    try {
      const r = await api.post("/admin/waitlist/remind-all");
      toast.success(`Reminded ${r.data.reminded} lead${r.data.reminded === 1 ? "" : "s"} (${r.data.emailed} emailed)`);
      load();
    } catch (e) { notifyError(e); } finally { setRemindAllBusy(false); }
  };
  const exportCsv = async () => {
    let cols;
    try { cols = (await api.get("/admin/csv-columns")).data.columns; }
    catch { cols = ["name", "company", "email", "phone", "status", "source", "campaign", "invited_code", "invite_emailed", "reminder_count", "downloaded_whitepaper", "created_at"]; }
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = [cols.join(",")].concat(visibleLeads.map((w) => cols.map((c) => esc(w[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${visibleLeads.length} lead${visibleLeads.length === 1 ? "" : "s"} to CSV`);
  };
  const buildCsv = (leads, cols) => {
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = [cols.join(",")].concat(leads.map((w) => cols.map((c) => esc(w[c])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };
  const segmentLeads = (seg) => {
    let list = seg.source_filter ? waitlist.filter((w) => csrc(w.source) === seg.source_filter) : waitlist.slice();
    const q = (seg.search || "").trim().toLowerCase();
    if (q) list = list.filter((w) => [w.name, w.company, w.email].some((f) => (f || "").toLowerCase().includes(q)));
    if (seg.sort === "name") list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    else if (seg.sort === "company") list.sort((a, b) => (a.company || "").localeCompare(b.company || ""));
    else if (seg.sort === "status") list.sort((a, b) => (a.status || "").localeCompare(b.status || ""));
    return list;
  };
  const resolveFollowUp = async (noteId) => {
    try { await api.post(`/admin/waitlist/notes/${noteId}/done`); toast.success("Follow-up cleared"); loadFollowUps(); }
    catch (e) { notifyError(e); }
  };
  const saveSegment = async () => {
    const name = segName.trim();
    if (!name) return;
    let columns;
    try { columns = (await api.get("/admin/csv-columns")).data.columns; } catch { columns = []; }
    try {
      await api.post("/admin/segments", { name, source_filter: sourceFilter, search, sort: sortBy, columns });
      setSegName(""); toast.success(`Segment "${name}" saved`); loadSegments();
    } catch (e) { notifyError(e); }
  };
  const applySegment = (seg) => { setSourceFilter(seg.source_filter || null); setSearch(seg.search || ""); setSortBy(seg.sort || "recent"); toast.message(`Loaded "${seg.name}"`); };
  const exportSegment = (seg) => { const leads = segmentLeads(seg); buildCsv(leads, seg.columns || []); toast.success(`Exported ${leads.length} from "${seg.name}"`); };
  const deleteSegment = async (id) => { try { await api.delete(`/admin/segments/${id}`); loadSegments(); } catch (e) { notifyError(e); } };

  return (
    <div className="space-y-6" data-testid="launch-admin">
      <div className="flex flex-wrap gap-2 border-b border-white/8 pb-3" data-testid="admin-tabs">
        {[["overview", "Overview"], ["leads", "Leads & Invites"], ["automation", "Automation"]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
            className={`text-sm px-3 py-1.5 rounded-lg transition ${tab === id ? "bg-[#FFB020]/15 text-[#FFB020]" : "text-white/55 hover:text-white"}`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (<>
      {/* Access analytics */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="access-stats">
          <Stat icon={Users} label="Waitlist size" value={stats.waitlist_total} sub={`${stats.founding_members} founding members`} accent="#5B8DEF" />
          <Stat icon={PaperPlaneTilt} label="Invites sent" value={stats.invites_sent} sub={`${stats.active_codes} active codes`} accent="#FFB020" />
          <Stat icon={Ticket} label="Activated" value={stats.activated} sub={`${stats.total_redemptions} code redemptions`} accent="#00A86B" />
          <Stat icon={TrendUp} label="Redemption rate" value={`${stats.redemption_rate}%`} sub="of invites activated" accent="#00A86B" />
        </div>
      )}

      {followUps.length > 0 && (
        <div className="glass rounded-2xl p-6" data-testid="follow-ups-card">
          <div className="flex items-center gap-2 eyebrow mb-4"><Bell size={15} weight="fill" style={{ color: "#E63946" }} /> Follow-ups {followUps.filter((f) => f.overdue).length > 0 && <span className="text-[#E63946]">({followUps.filter((f) => f.overdue).length} due)</span>}</div>
          <div className="space-y-2 max-h-64 overflow-auto">
            {followUps.map((f) => (
              <div key={f.note_id} className="flex items-center justify-between gap-2 rounded-lg bg-black/25 border border-white/5 p-2.5 text-sm" data-testid={`follow-up-${f.note_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${f.overdue ? "bg-[#E63946]/15 text-[#E63946]" : "bg-[#FFB020]/15 text-[#FFB020]"}`}>{f.follow_up_date}</span>
                    <button onClick={() => setDetailId(f.waitlist_id)} data-testid={`follow-up-open-${f.note_id}`} className="text-white/85 hover:text-white truncate underline">{f.lead_name || f.lead_email}</button>
                  </div>
                  <div className="text-white/45 text-xs truncate">{f.text}</div>
                </div>
                <button onClick={() => resolveFollowUp(f.note_id)} data-testid={`follow-up-done-${f.note_id}`} className="btn-ghost text-xs shrink-0">Done</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {analytics && <AccessAnalytics a={analytics} activeSource={sourceFilter} onSelectSource={setSourceFilter} />}
      </>)}

      {tab === "leads" && <CampaignLinkBuilder />}

      {tab === "automation" && (<>
      <FoundingPlanCard />

      <AutoReminderCard />

      <ReminderCopyCard />

      <CsvScheduleCard />

      <CohortCompareCard />

      <FollowupDigestCard />
      </>)}

      {tab === "leads" && (<>
      <div className="grid lg:grid-cols-2 gap-6">
      {/* Invite codes */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center gap-2 eyebrow mb-4"><Ticket size={15} weight="fill" style={{ color: "#FFB020" }} /> Invite codes</div>
        <form onSubmit={create} className="grid grid-cols-2 gap-2 mb-4" data-testid="invite-create-form">
          <input className="col-span-2" data-testid="invite-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (e.g. Beta cohort 1)" />
          <input type="number" min="1" data-testid="invite-max-uses" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="Max uses (blank = ∞)" />
          <input type="date" data-testid="invite-expires" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          <button type="submit" disabled={creating} data-testid="invite-create-btn" className="btn-gold col-span-2 justify-center text-sm">{creating ? "Creating…" : "Generate code"}</button>
        </form>
        <div className="space-y-2 max-h-72 overflow-auto">
          {codes.length === 0 ? <div className="text-white/40 text-sm py-4 text-center">No codes yet.</div> : codes.map((c) => (
            <div key={c.code_id} className="flex items-center justify-between rounded-lg bg-black/25 border border-white/5 p-2.5" data-testid={`invite-${c.code_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="mono text-sm text-[#FFB020]">{c.code}</span>
                  <button onClick={() => copy(c.code)} className="text-white/40 hover:text-white" data-testid={`invite-copy-${c.code_id}`}><Copy size={13} /></button>
                  {!c.active && <span className="text-[10px] uppercase text-[#E63946]">disabled</span>}
                </div>
                <div className="text-white/40 text-xs truncate">{c.note || "—"} · {c.uses}{c.max_uses ? `/${c.max_uses}` : ""} used{c.expires_at ? ` · exp ${c.expires_at.slice(0, 10)}` : ""}</div>
              </div>
              <button onClick={() => toggle(c.code_id)} data-testid={`invite-toggle-${c.code_id}`} className={`text-xs flex items-center gap-1 ${c.active ? "text-white/50 hover:text-[#E63946]" : "text-[#00A86B]"}`}><Power size={13} /> {c.active ? "Disable" : "Enable"}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Waitlist */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 eyebrow"><Users size={15} weight="fill" style={{ color: "#5B8DEF" }} /> Launch waitlist ({filtering ? `${visibleLeads.length} / ${waitlist.length}` : waitlist.length})</div>
          <div className="flex items-center gap-3">
            <button onClick={exportCsv} disabled={visibleLeads.length === 0} data-testid="waitlist-export-csv" className="text-xs text-white/50 hover:text-[#00A86B] flex items-center gap-1 disabled:opacity-40"><FileArrowDown size={13} /> Export CSV</button>
            {awaitingCount > 0 && (
              <button onClick={remindAll} disabled={remindAllBusy} data-testid="waitlist-remind-all" className="text-xs text-white/50 hover:text-[#FFB020] flex items-center gap-1 disabled:opacity-40"><Bell size={13} /> {remindAllBusy ? "Sending…" : `Remind awaiting (${awaitingCount})`}</button>
            )}
            {pendingIds.length > 0 && (
              <>
                <button onClick={selectAll} data-testid="waitlist-select-all" className="text-xs text-white/50 hover:text-white">{allSelected ? "Clear" : "Select all pending"}</button>
                <button onClick={inviteBulk} disabled={bulkBusy || selected.length === 0} data-testid="waitlist-invite-bulk" className="btn-gold text-xs disabled:opacity-40"><PaperPlaneTilt size={13} /> {bulkBusy ? "Sending…" : `Invite selected (${selected.length})`}</button>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-2 rounded-lg bg-black/25 border border-white/8 px-3 py-2" style={{ flex: 1, minWidth: 0 }}>
            <MagnifyingGlass size={14} className="text-white/40 shrink-0" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="waitlist-search" placeholder="Search name, company or email…" className="bg-transparent border-0 p-0 text-sm focus:ring-0" style={{ flex: 1, width: "auto", minWidth: 0, boxShadow: "none" }} />
            {search && <button onClick={() => setSearch("")} data-testid="waitlist-search-clear" className="text-white/40 hover:text-white text-xs shrink-0">✕</button>}
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="waitlist-sort" className="text-sm rounded-lg bg-black/25 border border-white/8 px-2 py-2 text-white/70 shrink-0" style={{ width: "auto" }}>
            <option value="recent">Most recent</option>
            <option value="name">Name A–Z</option>
            <option value="company">Company A–Z</option>
            <option value="status">Status</option>
          </select>
        </div>
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="segments-bar">
          <input value={segName} onChange={(e) => setSegName(e.target.value)} data-testid="segment-name" placeholder="Save current view as…" className="text-sm rounded-lg bg-black/25 border border-white/8 px-3 py-1.5" style={{ width: "auto", flex: "0 1 220px" }} />
          <button onClick={saveSegment} disabled={!segName.trim()} data-testid="segment-save" className="btn-ghost text-xs disabled:opacity-40">Save segment</button>
          {segments.map((s) => (
            <span key={s.segment_id} className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/10 pl-2 pr-1 py-0.5 text-xs" data-testid={`segment-${s.segment_id}`}>
              <button onClick={() => applySegment(s)} data-testid={`segment-load-${s.segment_id}`} className="text-white/75 hover:text-white">{s.name}</button>
              <button onClick={() => exportSegment(s)} data-testid={`segment-export-${s.segment_id}`} title="Export" className="text-white/40 hover:text-[#00A86B]"><FileArrowDown size={12} /></button>
              <button onClick={() => deleteSegment(s.segment_id)} data-testid={`segment-delete-${s.segment_id}`} title="Delete" className="text-white/30 hover:text-[#E63946]">✕</button>
            </span>
          ))}
        </div>
        {sourceFilter && (
          <div className="flex items-center justify-between rounded-lg bg-[#5B8DEF]/10 border border-[#5B8DEF]/25 px-3 py-2 mb-3 text-xs" data-testid="waitlist-source-filter">
            <span className="text-white/70">Showing leads from channel <span className="mono text-[#5B8DEF]">{sourceFilter}</span></span>
            <button onClick={() => setSourceFilter(null)} data-testid="waitlist-clear-filter" className="text-white/50 hover:text-white underline">Clear filter</button>
          </div>
        )}
        {waitlist.some((w) => w.invite_email_error === "domain not verified") && (
          <div className="rounded-lg bg-[#E63946]/10 border border-[#E63946]/30 p-3 mb-3 text-xs text-[#ffb0b6]" data-testid="domain-not-verified-banner">
            Invite emails aren&apos;t sending — the <span className="mono">percen360.com</span> domain isn&apos;t verified in Resend. Verify it at <span className="mono">resend.com/domains</span>. Until then, codes are still created — copy and send them manually.
          </div>
        )}
        <div className="space-y-2 max-h-96 overflow-auto" data-testid="waitlist-leads">
          {visibleLeads.length === 0 ? <div className="text-white/40 text-sm py-4 text-center">{sourceFilter ? "No leads from this channel yet." : "No signups yet."}</div> : visibleLeads.map((w) => (
            <div key={w.waitlist_id} className="rounded-lg bg-black/25 border border-white/5 p-2.5 text-sm" data-testid={`lead-${w.waitlist_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {w.status === "pending" && <input type="checkbox" checked={selected.includes(w.waitlist_id)} onChange={() => toggleSel(w.waitlist_id)} data-testid={`lead-select-${w.waitlist_id}`} style={{ width: "auto" }} />}
                  <span className="text-white/85 truncate">{w.name}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => setDetailId(w.waitlist_id)} data-testid={`lead-view-${w.waitlist_id}`} className="text-white/40 hover:text-white text-xs underline">View</button>
                  <span className={`text-[10px] uppercase tracking-wider ${w.status === "activated" ? "text-[#00A86B]" : w.status === "invited" ? "text-[#5B8DEF]" : "text-[#FFB020]"}`}>{w.status}</span>
                  {w.status === "pending" && (
                    <button onClick={() => invite(w)} disabled={inviting === w.waitlist_id} data-testid={`lead-invite-${w.waitlist_id}`} className="btn-ghost text-xs">
                      <PaperPlaneTilt size={13} /> {inviting === w.waitlist_id ? "Sending…" : "Send invite"}
                    </button>
                  )}
                  {w.status === "invited" && (
                    <button onClick={() => remind(w)} disabled={reminding === w.waitlist_id} data-testid={`lead-remind-${w.waitlist_id}`} className="btn-ghost text-xs">
                      <Bell size={13} /> {reminding === w.waitlist_id ? "Sending…" : "Remind"}
                    </button>
                  )}
                </div>
              </div>
              <div className="text-white/45 text-xs">{w.email}{w.company ? ` · ${w.company}` : ""}{w.phone ? ` · ${w.phone}` : ""}{w.source ? ` · ${w.source}` : ""}</div>
              <div className="flex items-center gap-2 mt-0.5">
                {w.downloaded_whitepaper && <span className="inline-flex items-center gap-1 text-[10px] text-[#5B8DEF]" data-testid={`lead-downloaded-${w.waitlist_id}`}><FileArrowDown size={11} weight="fill" /> downloaded white paper</span>}
                {w.invited_code && <span className="text-white/35 text-[11px] mono">invited: {w.invited_code}{w.invite_emailed === false ? ` · email not sent (${w.invite_email_error || "failed"})` : " · emailed"}</span>}
                {w.reminder_count > 0 && <span className="text-[#FFB020]/70 text-[11px]" data-testid={`lead-reminded-${w.waitlist_id}`}>· reminded ×{w.reminder_count}</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
      </>)}
      {detailId && <LeadDetailModal id={detailId} onClose={() => { setDetailId(null); loadFollowUps(); }} />}
    </div>
  );
}

function Breakdown({ title, rows }) {
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <div data-testid={`breakdown-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{title}</div>
      {rows.length === 0 ? <div className="text-white/30 text-xs py-2">No data yet</div> : (
        <div className="space-y-2">
          {rows.slice(0, 5).map((r) => (
            <div key={r.label}>
              <div className="flex justify-between text-xs mb-0.5"><span className="text-white/70">{r.label}</span><span className="mono text-white/50">{r.count}</span></div>
              <div className="h-1.5 rounded-full bg-white/8 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.round((r.count / total) * 100)}%`, background: "#5B8DEF" }} /></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AccessAnalytics({ a, activeSource, onSelectSource }) {
  return (
    <div className="glass rounded-2xl p-6" data-testid="access-analytics">
      <div className="flex items-center gap-2 eyebrow mb-4"><ChartLine size={15} weight="fill" style={{ color: "#00A86B" }} /> Waitlist & traffic analytics</div>

      {/* Traffic tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="traffic-tiles">
        <Stat icon={Eye} label="Landing views" value={a.visits_total ?? 0} sub={`${a.visits_window ?? 0} in last 30 days`} accent="#5B8DEF" />
        <Stat icon={FileArrowDown} label="White paper downloads" value={a.downloads_total ?? 0} sub={`${a.downloads_identified ?? 0} tied to a signup`} accent="#FFB020" />
        <Stat icon={Users} label="Waitlist signups" value={a.by_source?.reduce((s, r) => s + r.count, 0) ?? 0} sub="named leads" accent="#00A86B" />
        <Stat icon={TrendUp} label="View→signup" value={`${a.visits_total ? Math.min(100, Math.round(((a.by_source?.reduce((s, r) => s + r.count, 0) ?? 0) / a.visits_total) * 100)) : 0}%`} sub="conversion" accent="#00A86B" />
      </div>

      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">Landing views vs signups (30 days)</div>
      <div style={{ width: "100%", height: 160 }} data-testid="signups-chart">
        <ResponsiveContainer>
          <AreaChart data={a.series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="visGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#5B8DEF" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#5B8DEF" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="wlGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#00A86B" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#00A86B" stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fill: "#8A9A94", fontSize: 9 }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} interval={4} />
            <Tooltip contentStyle={{ background: "#0A1311", border: "1px solid #1B2624", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#AEB8B4" }} />
            <Area type="monotone" dataKey="visits" name="Views" stroke="#5B8DEF" strokeWidth={2} fill="url(#visGrad)" />
            <Area type="monotone" dataKey="signups" name="Signups" stroke="#00A86B" strokeWidth={2} fill="url(#wlGrad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-5 mt-6 pt-5 border-t border-white/5">
        <Breakdown title="Top referrers" rows={a.top_referrers || []} />
        <Breakdown title="Source" rows={a.by_source} />
        <Breakdown title="Funnel status" rows={a.by_status} />
        <Breakdown title="Company stage" rows={a.by_stage} />
        <Breakdown title="Jurisdiction" rows={a.by_jurisdiction} />
      </div>

      {a.campaigns && a.campaigns.length > 0 && (
        <div className="mt-6 pt-5 border-t border-white/5" data-testid="campaign-table">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Channels compared</div>
            <div className="text-white/35 text-[11px]">Tap a channel to filter the waitlist</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/40 text-[11px] uppercase tracking-wider">
                  <th className="text-left font-normal pb-2">Channel</th>
                  <th className="text-right font-normal pb-2">Views</th>
                  <th className="text-right font-normal pb-2">Signups</th>
                  <th className="text-right font-normal pb-2">Downloads</th>
                  <th className="text-right font-normal pb-2">Activated</th>
                  <th className="text-right font-normal pb-2">Conv.</th>
                </tr>
              </thead>
              <tbody>
                {a.campaigns.map((c) => (
                  <tr key={c.source} onClick={() => onSelectSource(activeSource === c.source ? null : c.source)}
                    className={`border-t border-white/5 cursor-pointer transition ${activeSource === c.source ? "bg-[#5B8DEF]/15" : "hover:bg-white/5"}`}
                    data-testid={`campaign-row-${c.source}`}>
                    <td className={`py-2 mono ${activeSource === c.source ? "text-[#5B8DEF]" : "text-white/85"}`}>{c.source}</td>
                    <td className="py-2 text-right text-white/60">{c.visits}</td>
                    <td className="py-2 text-right text-[#00A86B] font-semibold">{c.signups}</td>
                    <td className="py-2 text-right text-white/60">{c.downloads}</td>
                    <td className="py-2 text-right text-white/60">{c.activated}</td>
                    <td className="py-2 text-right text-[#FFB020]">{c.conversion}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-white/35 text-[11px] mt-2">Untagged traffic groups under &ldquo;direct&rdquo;. Tag your links (Campaign link builder) to split channels precisely.</div>
        </div>
      )}

      <ConversionFunnel />
    </div>
  );
}

function ConversionFunnel() {
  const [days, setDays] = useState(30);
  const [campaigns, setCampaigns] = useState(null);
  useEffect(() => {
    setCampaigns(null);
    api.get(`/admin/access-analytics?days=${days}`).then((r) => setCampaigns(r.data.campaigns || [])).catch(() => setCampaigns([]));
  }, [days]);
  const STAGES = [["Views", "visits", "#5B8DEF"], ["Signups", "signups", "#00A86B"], ["Invited", "invited", "#FFB020"], ["Activated", "activated", "#E9C46A"]];
  const rows = (campaigns || []).filter((c) => c.signups > 0).slice(0, 6);
  return (
    <div className="mt-6 pt-5 border-t border-white/5" data-testid="conversion-funnel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40"><Funnel size={13} weight="fill" style={{ color: "#E63946" }} /> Where signups stall — drop-off by channel</div>
        <div className="flex items-center gap-1" data-testid="funnel-timeframe">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`funnel-days-${d}`}
              className={`px-2.5 py-1 rounded-full text-[11px] border transition ${days === d ? "border-[#FFB020] text-[#FFB020] bg-[#FFB020]/10" : "border-white/15 text-white/50 hover:border-white/30"}`}>{d}d</button>
          ))}
        </div>
      </div>
      {campaigns === null ? (
        <div className="text-white/30 text-xs py-3">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-white/30 text-xs py-3">No channels with signups in the last {days} days.</div>
      ) : (
        <div className="space-y-4">
          {rows.map((c) => {
            const max = Math.max(c.visits, c.signups, c.invited, c.activated, 1);
            const vals = STAGES.map(([, k]) => c[k] || 0);
            let worstIdx = -1; let worstKeep = 1;
            for (let i = 1; i < vals.length; i++) {
              if (vals[i - 1] > 0) { const keep = vals[i] / vals[i - 1]; if (keep < worstKeep) { worstKeep = keep; worstIdx = i; } }
            }
            return (
              <div key={c.source} data-testid={`funnel-${c.source}`}>
                <div className="flex justify-between items-center mb-1.5">
                  <span className="text-white/80 text-sm mono">{c.source}</span>
                  {worstIdx > 0 && <span className="text-[11px] text-[#E63946]" data-testid={`funnel-drop-${c.source}`}>biggest drop: {STAGES[worstIdx - 1][0]}→{STAGES[worstIdx][0]} (−{Math.round((1 - worstKeep) * 100)}%)</span>}
                </div>
                <div className="grid grid-cols-4 gap-1.5">
                  {STAGES.map(([label, k, color], i) => {
                    const val = c[k] || 0;
                    const prev = i > 0 ? (c[STAGES[i - 1][1]] || 0) : null;
                    const keep = prev != null ? (prev > 0 ? Math.round((val / prev) * 100) : 0) : null;
                    return (
                      <div key={k}>
                        <div className="h-1.5 rounded-full bg-white/8 overflow-hidden mb-1"><div className="h-full rounded-full" style={{ width: `${Math.round((val / max) * 100)}%`, background: color }} /></div>
                        <div className="text-[10px] text-white/50 flex justify-between"><span>{label}</span><span className="mono text-white/70">{val}</span></div>
                        {keep != null && <div className="text-[9px] text-white/35 mono">{keep}% kept</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CampaignLinkBuilder() {
  const PRESETS = ["linkedin", "twitter", "newsletter", "slack", "email", "webinar"];
  const [source, setSource] = useState("");
  const [campaign, setCampaign] = useState("");

  const cleanSource = source.trim().toLowerCase().replace(/[^a-z0-9._-]+/g, "");
  const cleanCampaign = campaign.trim();
  const base = window.location.origin;
  const params = new URLSearchParams();
  if (cleanSource) params.set("ref", cleanSource);
  if (cleanCampaign) params.set("utm_campaign", cleanCampaign);
  const url = cleanSource ? `${base}/?${params.toString()}` : "";

  const copy = () => {
    if (!url) { toast.error("Enter a source first"); return; }
    navigator.clipboard?.writeText(url);
    toast.success("Campaign link copied");
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="campaign-builder">
      <div className="flex items-center gap-2 eyebrow mb-2"><ChartLine size={15} weight="fill" style={{ color: "#5B8DEF" }} /> Campaign link builder</div>
      <p className="text-white/55 text-sm mb-4">Make a tagged share link so a visit and any signup are attributed to the right channel in Top referrers.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {PRESETS.map((p) => (
          <button key={p} onClick={() => setSource(p)} data-testid={`cb-preset-${p}`}
            className={`px-3 py-1 rounded-full text-xs border transition ${cleanSource === p ? "border-[#FFB020] text-[#FFB020] bg-[#FFB020]/10" : "border-white/15 text-white/60 hover:border-white/30"}`}>{p}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label>Source (channel) *</label><input data-testid="cb-source" value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. linkedin" /></div>
        <div><label>Campaign (optional)</label><input data-testid="cb-campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)} placeholder="e.g. launch-teaser" /></div>
      </div>
      <div className="mt-4">
        <label>Your shareable link</label>
        <div className="flex items-center gap-2 rounded-lg bg-black/30 border border-white/8 p-3">
          <span data-testid="cb-url" className="mono text-sm text-[#5B8DEF] truncate flex-1">{url || "Enter a source to build your link…"}</span>
          <button onClick={copy} disabled={!url} data-testid="cb-copy" className="btn-gold text-xs shrink-0"><Copy size={13} /> Copy</button>
        </div>
        {cleanSource && <div className="text-white/40 text-xs mt-2">Shows in Top referrers as <span className="text-[#FFB020] mono">{cleanSource}</span>{cleanCampaign ? <> · campaign <span className="text-white/60 mono">{cleanCampaign}</span></> : null}</div>}
      </div>
    </div>
  );
}

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function LeadDetailModal({ id, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [adding, setAdding] = useState(false);
  const load = () => { api.get(`/admin/waitlist/${id}`).then((r) => setData(r.data)).catch(() => setErr(true)); };
  useEffect(() => { setData(null); setErr(false); load(); }, [id]);
  const addNote = async () => {
    const t = noteText.trim();
    if (!t) return;
    setAdding(true);
    try { await api.post(`/admin/waitlist/${id}/notes`, { text: t, follow_up_date: followUp || null }); setNoteText(""); setFollowUp(""); load(); }
    catch (e) { notifyError(e); } finally { setAdding(false); }
  };
  const delNote = async (noteId) => {
    try { await api.delete(`/admin/waitlist/notes/${noteId}`); load(); } catch (e) { notifyError(e); }
  };
  const ICON = { signup: Users, download: FileArrowDown, invite: PaperPlaneTilt, reminder: Bell, activated: Sparkle, note: NotePencil };
  const COLOR = { signup: "#5B8DEF", download: "#5B8DEF", invite: "#FFB020", reminder: "#FFB020", activated: "#00A86B", note: "#AEB8B4" };
  const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4" style={{ zIndex: 50 }} onClick={onClose} data-testid="lead-detail-modal">
      <div className="glass rounded-2xl p-6 w-full max-w-lg max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 eyebrow"><Users size={15} weight="fill" style={{ color: "#5B8DEF" }} /> Lead journey</div>
          <button onClick={onClose} data-testid="lead-detail-close" className="text-white/50 hover:text-white text-lg">✕</button>
        </div>
        {err ? <div className="text-[#E63946] text-sm py-6 text-center">Could not load this lead.</div>
          : !data ? <div className="text-white/40 text-sm py-6 text-center">Loading…</div>
            : (
              <>
                <div className="mb-4">
                  <div className="text-white/90 text-lg">{data.lead.name || "—"}</div>
                  <div className="text-white/45 text-sm">{data.lead.email}{data.lead.company ? ` · ${data.lead.company}` : ""}</div>
                  <div className="text-white/40 text-xs mt-1">Status: <span className="uppercase">{data.lead.status}</span>{data.lead.source ? ` · source ${data.lead.source}` : ""}{data.lead.campaign ? ` · ${data.lead.campaign}` : ""}</div>
                </div>
                <div className="relative pl-5" data-testid="lead-timeline">
                  <div className="absolute left-[6px] top-1 bottom-1 w-px bg-white/10" />
                  {data.timeline.length === 0 ? <div className="text-white/30 text-sm">No events yet.</div> : data.timeline.map((e, i) => {
                    const Icon = ICON[e.type] || Sparkle;
                    return (
                      <div key={i} className="relative mb-4" data-testid={`timeline-${e.type}`}>
                        <div className="absolute -left-5 top-0.5 w-3 h-3 rounded-full" style={{ background: COLOR[e.type] || "#5B8DEF" }} />
                        <div className="flex items-center gap-2">
                          <Icon size={13} style={{ color: COLOR[e.type] }} />
                          <span className="text-white/85 text-sm">{e.label}</span>
                          {e.type === "note" && <button onClick={() => delNote(e.note_id)} data-testid={`note-del-${e.note_id}`} className="text-white/30 hover:text-[#E63946] text-xs ml-auto">delete</button>}
                        </div>
                        {e.type === "note"
                          ? <div className="text-white/70 text-sm mt-0.5 whitespace-pre-wrap">{e.detail}<span className="text-white/35 text-xs"> · {fmt(e.at)}{e.author ? ` · ${e.author}` : ""}</span>{e.follow_up_date && !e.follow_up_done && <span className="ml-1 text-[10px] uppercase px-1.5 py-0.5 rounded bg-[#FFB020]/15 text-[#FFB020]" data-testid={`timeline-followup-${e.note_id}`}>follow up {e.follow_up_date}</span>}</div>
                          : <div className="text-white/40 text-xs">{fmt(e.at)}{e.detail ? ` · ${e.detail}` : ""}</div>}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-white/8">
                  <label>Add a private note</label>
                  <textarea rows={2} data-testid="lead-note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="e.g. Called on Tue — will activate after payroll run" />
                  <div className="flex items-center justify-between gap-2 mt-2">
                    <div className="flex items-center gap-2 text-xs text-white/45">
                      <span>Follow up on</span>
                      <input type="date" data-testid="lead-note-followup" value={followUp} onChange={(e) => setFollowUp(e.target.value)} style={{ width: "auto" }} />
                    </div>
                    <button onClick={addNote} disabled={adding || !noteText.trim()} data-testid="lead-note-add" className="btn-gold text-sm disabled:opacity-40">{adding ? "Saving…" : "Add note"}</button>
                  </div>
                </div>
              </>
            )}
      </div>
    </div>
  );
}

function CohortCol({ label, d, testid }) {
  return (
    <div className="flex-1 rounded-xl bg-black/25 border border-white/8 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">{label}</div>
      <div className="text-3xl font-bold" style={{ color: "#00A86B" }}>{d.rate}%</div>
      <div className="text-white/45 text-xs mt-1">{d.activated} of {d.invited} invited activated</div>
    </div>
  );
}

function FollowupDigestCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/admin/followup-digest").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const save = async (patch) => {
    setSaving(true);
    try {
      const next = { ...cfg, ...patch };
      const r = await api.put("/admin/followup-digest", { enabled: next.enabled, email: next.email });
      setCfg(r.data);
      toast.success(r.data.enabled ? "Morning digest scheduled" : "Digest saved (off)");
    } catch (e) { notifyError(e); } finally { setSaving(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/followup-digest/send-now");
      if (r.data.count === 0) toast.message("No follow-ups due today — nothing to send.");
      else if (r.data.emailed) toast.success(`Digest with ${r.data.count} follow-up${r.data.count === 1 ? "" : "s"} emailed to ${r.data.to}`);
      else if (r.data.email_error === "domain not verified") toast.error("Not sent — verify percen360.com at resend.com/domains.");
      else toast.message(`Not sent (${r.data.email_error || "failed"}).`);
      const g = await api.get("/admin/followup-digest"); setCfg(g.data);
    } catch (e) { notifyError(e); } finally { setSending(false); }
  };
  return (
    <div className="glass rounded-2xl p-6" data-testid="followup-digest-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 eyebrow"><Bell size={15} weight="fill" style={{ color: "#E63946" }} /> Morning follow-up digest</div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.enabled ? "bg-[#00A86B]/15 text-[#00A86B]" : "bg-white/8 text-white/50"}`} data-testid="followup-digest-state">{cfg.enabled ? "On" : "Off"}</span>
      </div>
      <p className="text-white/55 text-sm mb-4">Get a short email each morning listing the leads due for follow-up that day, so nothing slips through.</p>
      <div><label>Send to email</label><input type="email" data-testid="fd-email" value={cfg.email} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} placeholder="you@percen360.com" /></div>
      {cfg.last_sent_at && <div className="text-white/35 text-xs mt-2" data-testid="fd-last-sent">Last sent: {new Date(cfg.last_sent_at).toLocaleString()}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={sendNow} disabled={sending || !cfg.email} data-testid="fd-send-now" className="btn-ghost text-sm">{sending ? "Sending…" : "Send now"}</button>
        <button onClick={() => save({})} disabled={saving} data-testid="fd-save" className="btn-ghost text-sm">Save</button>
        {cfg.enabled
          ? <button onClick={() => save({ enabled: false })} disabled={saving} data-testid="fd-disable" className="btn-ghost text-sm text-[#E63946]">Turn off</button>
          : <button onClick={() => save({ enabled: true })} disabled={saving} data-testid="fd-enable" className="btn-gold text-sm">Turn on digest</button>}
      </div>
    </div>
  );
}

function CohortCompareCard() {
  const [c, setC] = useState(null);
  const [trend, setTrend] = useState(null);
  useEffect(() => {
    api.get("/admin/cohorts").then((r) => setC(r.data)).catch(() => {});
    api.get("/admin/cohort-trend?weeks=8").then((r) => setTrend(r.data.weeks || [])).catch(() => {});
  }, []);
  if (!c) return null;
  const up = c.rate_delta >= 0;
  return (
    <div className="glass rounded-2xl p-6" data-testid="cohort-compare-card">
      <div className="flex items-center gap-2 eyebrow mb-4"><TrendUp size={15} weight="fill" style={{ color: "#00A86B" }} /> Activation momentum</div>
      <div className="flex gap-3">
        <CohortCol label="This week" d={c.this_week} testid="cohort-this-week" />
        <CohortCol label="Last week" d={c.last_week} testid="cohort-last-week" />
      </div>
      <div className="mt-3 text-sm flex items-center gap-2" data-testid="cohort-delta">
        <span style={{ color: up ? "#00A86B" : "#E63946" }}>{up ? "▲" : "▼"} {Math.abs(c.rate_delta)} pts</span>
        <span className="text-white/45">activation rate vs last week · {c.invited_delta >= 0 ? "+" : ""}{c.invited_delta} invited</span>
      </div>
      {trend && trend.length > 0 && (
        <div className="mt-5 pt-4 border-t border-white/8" data-testid="cohort-trend">
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-2">Activation rate — last {trend.length} weekly cohorts</div>
          <div style={{ width: "100%", height: 140 }}>
            <ResponsiveContainer>
              <AreaChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="cohortRate" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00A86B" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#00A86B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="week_start" tick={{ fill: "#6B7770", fontSize: 10 }} tickFormatter={(d) => (d || "").slice(5)} />
                <Tooltip contentStyle={{ background: "#0C1614", border: "1px solid #1B2624", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#AEB8B4" }} formatter={(v, _n, p) => [`${v}% (${p.payload.activated}/${p.payload.invited})`, "Activation"]} />
                <Area type="monotone" dataKey="rate" stroke="#00A86B" strokeWidth={2} fill="url(#cohortRate)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

function ReminderCopyCard() {
  const [copy, setCopy] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/reminder-copy").then((r) => setCopy(r.data.copy)).catch(() => {}); }, []);
  if (!copy) return null;
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/admin/reminder-copy", copy); setCopy(r.data.copy); toast.success("Reminder email updated"); }
    catch (e) { notifyError(e); } finally { setSaving(false); }
  };
  const field = (k, label, textarea) => (
    <div>
      <label>{label}</label>
      {textarea
        ? <textarea rows={3} data-testid={`rc-${k}`} value={copy[k]} onChange={(e) => setCopy({ ...copy, [k]: e.target.value })} />
        : <input data-testid={`rc-${k}`} value={copy[k]} onChange={(e) => setCopy({ ...copy, [k]: e.target.value })} />}
    </div>
  );
  return (
    <div className="glass rounded-2xl p-6" data-testid="reminder-copy-card">
      <div className="flex items-center gap-2 eyebrow mb-2"><PaperPlaneTilt size={15} weight="fill" style={{ color: "#FFB020" }} /> Reminder email wording</div>
      <p className="text-white/55 text-sm mb-4">Edit the nudge invited leads receive (manual or automatic). Use <span className="mono text-[#FFB020]">{"{first}"}</span> to drop in their first name — the invite code and button link are added automatically.</p>
      <div className="space-y-3">
        {field("subject", "Subject line")}
        {field("heading", "Heading")}
        {field("body", "Body", true)}
        {field("cta", "Button label")}
      </div>
      <div className="flex justify-end mt-4"><button onClick={save} disabled={saving} data-testid="rc-save" className="btn-gold text-sm">{saving ? "Saving…" : "Save wording"}</button></div>
    </div>
  );
}

function CsvScheduleCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [cols, setCols] = useState(null);
  const [allCols, setAllCols] = useState([]);
  const [colSaving, setColSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/csv-schedule").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/csv-columns").then((r) => { setCols(r.data.columns); setAllCols(r.data.available); }).catch(() => {});
  }, []);
  if (!cfg) return null;
  const save = async (patch) => {
    setSaving(true);
    try {
      const next = { ...cfg, ...patch };
      const r = await api.put("/admin/csv-schedule", { enabled: next.enabled, email: next.email, weekday: Number(next.weekday) });
      setCfg(r.data);
      toast.success(r.data.enabled ? "Weekly export scheduled" : "Weekly export saved (off)");
    } catch (e) { notifyError(e); } finally { setSaving(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/csv-schedule/send-now");
      if (r.data.emailed) toast.success(`Waitlist CSV emailed to ${r.data.to}`);
      else if (r.data.email_error === "domain not verified") toast.error("Not sent — verify percen360.com at resend.com/domains.");
      else toast.message(`Not sent (${r.data.email_error || "failed"}).`);
      const g = await api.get("/admin/csv-schedule"); setCfg(g.data);
    } catch (e) { notifyError(e); } finally { setSending(false); }
  };
  const toggleCol = (c) => setCols((cur) => cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]);
  const saveCols = async () => {
    setColSaving(true);
    try { const r = await api.put("/admin/csv-columns", { columns: cols }); setCols(r.data.columns); toast.success("CSV columns saved"); }
    catch (e) { notifyError(e); } finally { setColSaving(false); }
  };
  return (
    <div className="glass rounded-2xl p-6" data-testid="csv-schedule-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 eyebrow"><FileArrowDown size={15} weight="fill" style={{ color: "#00A86B" }} /> Weekly CSV export</div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.enabled ? "bg-[#00A86B]/15 text-[#00A86B]" : "bg-white/8 text-white/50"}`} data-testid="csv-schedule-state">{cfg.enabled ? "On" : "Off"}</span>
      </div>
      <p className="text-white/55 text-sm mb-4">Email yourself the full waitlist as a CSV once a week, so you always have a fresh copy for outreach or your CRM.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label>Send to email</label><input type="email" data-testid="csv-email" value={cfg.email} onChange={(e) => setCfg({ ...cfg, email: e.target.value })} placeholder="you@percen360.com" /></div>
        <div><label>Day of week</label><select data-testid="csv-weekday" value={cfg.weekday} onChange={(e) => setCfg({ ...cfg, weekday: e.target.value })}>{DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></div>
      </div>
      {cols && (
        <div className="mt-4" data-testid="csv-columns-picker">
          <label>Columns in the export (applies to download + schedule)</label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {allCols.map((c) => (
              <button key={c} onClick={() => toggleCol(c)} data-testid={`csv-col-${c}`}
                className={`px-2 py-1 rounded-full text-[11px] border transition ${cols.includes(c) ? "border-[#00A86B] text-[#00A86B] bg-[#00A86B]/10" : "border-white/15 text-white/40 hover:border-white/30"}`}>{c}</button>
            ))}
          </div>
          <div className="flex justify-end mt-2"><button onClick={saveCols} disabled={colSaving || cols.length === 0} data-testid="csv-cols-save" className="btn-ghost text-xs disabled:opacity-40">{colSaving ? "Saving…" : "Save columns"}</button></div>
        </div>
      )}
      {cfg.last_sent_at && <div className="text-white/35 text-xs mt-2" data-testid="csv-last-sent">Last sent: {new Date(cfg.last_sent_at).toLocaleString()}</div>}
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={sendNow} disabled={sending || !cfg.email} data-testid="csv-send-now" className="btn-ghost text-sm">{sending ? "Sending…" : "Send now"}</button>
        <button onClick={() => save({})} disabled={saving} data-testid="csv-save" className="btn-ghost text-sm">Save</button>
        {cfg.enabled
          ? <button onClick={() => save({ enabled: false })} disabled={saving} data-testid="csv-disable" className="btn-ghost text-sm text-[#E63946]">Turn off</button>
          : <button onClick={() => save({ enabled: true })} disabled={saving} data-testid="csv-enable" className="btn-gold text-sm">Turn on weekly export</button>}
      </div>
    </div>
  );
}

function AutoReminderCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/auto-reminder").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;

  const save = async (patch) => {
    setSaving(true);
    try {
      const next = { ...cfg, ...patch };
      const r = await api.put("/admin/auto-reminder", { enabled: next.enabled, delay_days: Number(next.delay_days), max_reminders: Number(next.max_reminders) });
      setCfg(r.data);
      toast.success(r.data.enabled ? "Auto-reminders are on" : "Auto-reminders saved (off)");
    } catch (e) { notifyError(e); } finally { setSaving(false); }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="auto-reminder-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 eyebrow"><Bell size={15} weight="fill" style={{ color: "#FFB020" }} /> Auto reminders</div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${cfg.enabled ? "bg-[#00A86B]/15 text-[#00A86B]" : "bg-white/8 text-white/50"}`} data-testid="auto-reminder-state">{cfg.enabled ? "On" : "Off"}</span>
      </div>
      <p className="text-white/55 text-sm mb-4">Automatically nudge invited leads who haven&apos;t activated yet — a daily job re-sends their code once the delay below passes, up to the max per lead, so you don&apos;t have to press Remind by hand.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label>Nudge after (days)</label><input type="number" min="1" max="30" data-testid="ar-delay" value={cfg.delay_days} onChange={(e) => setCfg({ ...cfg, delay_days: e.target.value })} /></div>
        <div><label>Max auto-nudges per lead</label><input type="number" min="1" max="5" data-testid="ar-max" value={cfg.max_reminders} onChange={(e) => setCfg({ ...cfg, max_reminders: e.target.value })} /></div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={() => save({})} disabled={saving} data-testid="ar-save" className="btn-ghost text-sm">Save</button>
        {cfg.enabled
          ? <button onClick={() => save({ enabled: false })} disabled={saving} data-testid="ar-disable" className="btn-ghost text-sm text-[#E63946]">Turn off</button>
          : <button onClick={() => save({ enabled: true })} disabled={saving} data-testid="ar-enable" className="btn-gold text-sm">Turn on auto-reminders</button>}
      </div>
    </div>
  );
}

function FoundingPlanCard() {
  const [plan, setPlan] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/founding-plan").then((r) => setPlan(r.data)).catch(() => {}); }, []);
  if (!plan) return null;

  const save = async (enabled) => {
    setSaving(true);
    try {
      const payload = {
        name: plan.name || "Founding",
        monthly: plan.monthly ? Number(plan.monthly) : null,
        annual: plan.annual ? Number(plan.annual) : null,
        enabled: enabled != null ? enabled : plan.enabled,
      };
      const r = await api.put("/admin/founding-plan", payload);
      setPlan(r.data);
      toast.success(r.data.enabled ? "Founding pricing is live" : "Founding pricing saved (hidden)");
    } catch (e) { notifyError(e); } finally { setSaving(false); }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="founding-plan-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 eyebrow"><Sparkle size={15} weight="fill" style={{ color: "#FFB020" }} /> Founding pricing</div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${plan.enabled ? "bg-[#00A86B]/15 text-[#00A86B]" : "bg-white/8 text-white/50"}`} data-testid="founding-plan-state">{plan.enabled ? "Live" : "Hidden"}</span>
      </div>
      <p className="text-white/55 text-sm mb-4">Set the price founding members convert to after their free founding period. Hidden until you enable it.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div><label>Plan name</label><input data-testid="fp-name" value={plan.name || ""} onChange={(e) => setPlan({ ...plan, name: e.target.value })} placeholder="Founding" /></div>
        <div><label>Monthly (€)</label><input type="number" step="1" data-testid="fp-monthly" value={plan.monthly ?? ""} onChange={(e) => setPlan({ ...plan, monthly: e.target.value })} placeholder="e.g. 149" /></div>
        <div><label>Annual (€)</label><input type="number" step="1" data-testid="fp-annual" value={plan.annual ?? ""} onChange={(e) => setPlan({ ...plan, annual: e.target.value })} placeholder="e.g. 1490" /></div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={() => save(null)} disabled={saving} data-testid="fp-save" className="btn-ghost text-sm">Save draft</button>
        {plan.enabled
          ? <button onClick={() => save(false)} disabled={saving} data-testid="fp-disable" className="btn-ghost text-sm text-[#E63946]">Disable</button>
          : <button onClick={() => save(true)} disabled={saving} data-testid="fp-enable" className="btn-gold text-sm">Enable founding pricing</button>}
      </div>
    </div>
  );
}
